import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import useAppStore from "@/context/store";
import {
  checkPinExisted,
  createPin,
  fetchFileByLink,
  uploadFile,
} from "@/services";
import { filterBoards } from "@/utils/filterBoards";
import { getContentType } from "@/utils/getContentType";
import sortBoardsByTitleAndCreateTime, {
  setBoardsTitlePinyin,
} from "@/utils/sortBoardsByTitleAndCreateTime";
import useRequest from "@/utils/useReq";
import { useMemoizedFn } from "ahooks";
import classNames from "classnames";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Else, If, Then, When } from "react-if";
import HBIcon from "../HBIcon";
import HBLoading from "../HBLoading";
import HBSearchInput from "../HBSearchInput";
import PinCreatorBoardList from "./PinCreatorBoardList";
import styles from "./index.module.less";

const LAST_BOARD_NUM = 5;
/**
 *
 * @param {{
 *  pin?: IPin;
 *  imgData?: import("./SinglePinCreator").SinglePinCreatorProps["imageData"];
 *  onOk?: (pin: IPin) => void;
 *  form?: import('react').MutableRefObject<any>;
 * }} param0
 * @returns
 */
export default function PinCreatorList({ pin, imgData, onOk, form }) {
  const pinId = pin?.pin_id;
  const [keyword, setKeyword] = useState("");
  const searchInputRef = useRef(null);
  const { lastBoards: userLastBoards, setLastBoards } = useAppStore(
    ({ lastBoards, setLastBoards }) => ({
      lastBoards,
      setLastBoards,
    })
  );
  const abortControllerRef = useRef(null);

  useMemo(async () => {
    if (userLastBoards?.length <= 0) {
      const userLastBoards = await useAppStore.getState().refreshLastBoards();
      if (userLastBoards) {
        try {
          window.localStorage.setItem(
            LOCAL_STORAGE_KEY.UPDATE_LAST_BOARDS,
            JSON.stringify(userLastBoards)
          );
        } catch (e) {
          console.error("设置更新最近采集失败");
        }
      }
    }
  }, [userLastBoards?.length]);

  const { data: boardData, loading: boardLoading } = useRequest(
    async () => {
      // const result = await getLastBoards({ extra: "cover" });
      // 将画板增加拼音
      let boardList = userLastBoards;
      if (boardList?.length > 0 && !boardList?.[0]?._pinyin) {
        boardList = await setBoardsTitlePinyin(boardList);
        setLastBoards(boardList);
        return;
      }
      // 过滤原创画板
      const boards =
        boardList?.filter((board) => !board?.extra?.is_creation) || [];
      // 为画板添加首字母与拼音
      const withPinyinBoards = boards.map((board) => {
        const item = { ...board };
        let firstLetter = `${
          item._pinyin[0]?.length > 0
            ? item._pinyin[0][0]
            : item._pinyin[0] || ""
        }`.toLocaleUpperCase();
        if (!/[A-Z]/.test(firstLetter)) {
          firstLetter = "#";
        }
        item._firstLetter = firstLetter;
        return item;
      });
      // 前5个画板作为历史画板
      const lastBoards = withPinyinBoards
        .slice(0, LAST_BOARD_NUM)
        .map((board, index) => {
          const item = {
            ...board,
          };
          if (index === 0) {
            item._icon = "time";
          }
          return item;
        });
      // 剩余画板按字母排序
      const otherBoards = Object.entries(
        _.groupBy(
          sortBoardsByTitleAndCreateTime(
            withPinyinBoards.slice(LAST_BOARD_NUM, withPinyinBoards.length)
          ),
          "_firstLetter"
        )
      )
        .sort((a, b) => `${a[0]}`.charCodeAt() - `${b[0]}`.charCodeAt())
        .reduce((result, [firstLetter, group]) => {
          result = result.concat(
            group.map((board, index) => {
              const item = { ...board };
              if (index === 0) {
                item._icon = firstLetter;
              }
              return item;
            })
          );
          return result;
        }, []);

      return {
        groupByLetterBoards: [...lastBoards, ...otherBoards],
        withPinyinBoards,
      };
    },
    {
      cacheKey: "pinCreatorBoards",
      refreshDeps: [userLastBoards],
    }
  );

  const preparePromise = useRef();
  const prepareUpload = useMemoizedFn(async () => {
    let file_id;
    if (imgData.media !== "base64image") {
      let res = await fetchFileByLink({
        media: imgData.media,
        referer: imgData.url,
      });

      file_id = res.file_id;
    }

    if (!file_id) {
      const file = await uploadFile(imgData.base64);
      file_id = file.id;
    }

    return file_id;
  });
  // 提前上传图片获取file_id， 避免卡顿
  useEffect(() => {
    imgData?.base64 && (preparePromise.current = prepareUpload());
  }, [imgData?.base64, prepareUpload]);

  const createPinByImgData = useMemoizedFn(async (boardId, other) => {
    const fileId = await preparePromise.current;
    if (!fileId) {
      return;
    }
    return createPin({
      board_id: boardId,
      via: imgData.via,
      file_id: fileId,
      link: imgData.url,
      media_type: imgData.media_type,
      ...other,
    });
  });

  const onSelectBoard = useCallback(
    async (boardId) => {
      const values = form.current?.getFieldsValue(true) || null;
      let result;
      if (pin) {
        result = await createPin({
          board_id: boardId,
          via: pin.pin_id,
          ...values,
        });
      } else {
        result = await createPinByImgData(boardId, values);
      }

      if (result && !result.err) {
        onOk && onOk(result.pin);
      }
    },
    [createPinByImgData, form, onOk, pin]
  );

  const onKeywordChange = useCallback((evt) => {
    const keyword = evt.target.value;
    setKeyword(keyword.slice(0, 32));
  }, []);

  const { data: existPin } = useRequest(
    async () => {
      if (!pinId) {
        return null;
      }

      const result = await checkPinExisted({
        pin_id: pinId,
      });
      return result?.exist_pin;
    },
    {
      refreshDeps: [pinId],
    }
  );

  const groupByLetterBoards = useMemo(() => {
    return boardData?.groupByLetterBoards || [];
  }, [boardData]);

  const searchBoards = useMemo(() => {
    let boards = filterBoards(keyword, boardData);

    return boards.map((board, index) => {
      const item = { ...board };
      if (index === 0) {
        item._icon = "search";
      }

      return item;
    });
  }, [boardData, keyword]);

  useEffect(() => {
    searchInputRef.current?.input?.focus();

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      abortControllerRef.current?.abort();
    };
  }, []);

  return (
    <div
      className={classNames([styles.board, existPin ? styles.existPin : null])}
      data-content-type={getContentType(pin?.extra)}
    >
      <div className={styles.title}>采集</div>
      <div className={styles.search}>
        <HBSearchInput
          hideSearchBtn
          value={keyword}
          onChange={onKeywordChange}
          placeholder="搜索画板"
          ref={searchInputRef}
        />
      </div>
      <When condition={existPin}>
        <div className={styles.warning}>
          <HBIcon className={styles.icon} type="ic_Warning" />
          <div className={styles.text}>该图已在画板</div>
          <a
            className={styles.link}
            href={`/boards/${existPin?.board?.board_id}`}
            target="_blank"
            rel="noreferrer"
          >
            {existPin?.board?.title}
          </a>
        </div>
      </When>
      <If condition={boardLoading && !boardData}>
        <Then>
          <div className={styles.loading}>
            <HBLoading size="24px" />
          </div>
        </Then>
        <Else>
          <PinCreatorBoardList
            dataSource={keyword ? searchBoards : groupByLetterBoards}
            keyword={keyword}
            onSelectBoard={onSelectBoard}
            pin={pin}
          />
        </Else>
      </If>
    </div>
  );
}
